import { useCallback } from 'react';

import { useToast } from '../../../app/provider/useToast';
import { appConfig } from '../../../config/app-config';
import { addItems } from '../../cart/services/addItem';
import { generateAssociationKey } from '../../cart/utils/cartKey';
import type { ProductWithAddOns } from '../../product/types/product.types';
import { SHARE_SUCCESS_MESSAGES } from '../../shared-basket/constants/messages';
import type { CustomerGroup } from '../types/home.types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface BasketSelection {
  addOns?: unknown[];
  options?: unknown[];
  option?: { name?: string };
}

interface UseBasketActionParams {
  selectedPass: ProductWithAddOns | null;
  selectedRateId: string | null;
  customerGroups: CustomerGroup[];
  quantity: number;
  /** Called after a successful add-to-cart so the parent can reset UI state. */
  onSuccess: () => void;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------
export function useBasketAction({
  selectedPass,
  selectedRateId,
  customerGroups,
  quantity,
  onSuccess,
}: UseBasketActionParams) {
  const { showSuccess } = useToast();

  const handleAddToBasket = useCallback(
    async (selection: BasketSelection) => {
      const typedSelection = selection as {
        addOns?: ProductWithAddOns[];
        options?: Array<{ name?: string } | string>;
        option?: { name?: string };
      };

      if (!selectedPass) return;

      try {
        const addOnsToAdd = typedSelection?.addOns ?? [];
        let priceObject = selectedPass.masterVariant?.prices?.[0];

        if (selectedPass.masterVariant?.prices?.some((p) => p.customerGroup) && selectedRateId) {
          let matched = selectedPass.masterVariant.prices?.find(
            (p) => p.customerGroup && p.customerGroup.id === selectedRateId
          );
          if (!matched && Array.isArray(customerGroups)) {
            const selectedGroup = customerGroups.find((g) => g.id === selectedRateId);
            if (selectedGroup?.key) {
              matched = selectedPass.masterVariant.prices?.find(
                (p) => p.customerGroup?.obj?.key === selectedGroup.key
              );
            }
          }
          priceObject = matched ?? priceObject;
        }

        const basePrice =
          priceObject?.discounted?.value?.centAmount ?? priceObject?.value?.centAmount ?? 0;
        const currencyCode = priceObject?.value?.currencyCode ?? 'USD';

        const optionPayload = typedSelection?.options;
        const optionNames = Array.isArray(optionPayload)
          ? optionPayload
            .map((opt) =>
              typeof opt === 'object'
                ? (
                  (opt as { optionName?: string; name?: string })?.optionName ||
                  (opt as { name?: string })?.name
                )
                : opt
            )
            .filter(Boolean) as string[]
          : typedSelection?.option?.name
            ? [typedSelection.option.name]
            : [];

        let delegateRateTitle = '';
        if (selectedRateId && Array.isArray(customerGroups)) {
          const selectedGroup = customerGroups.find((g) => g.id === selectedRateId);
          if (selectedGroup) {
            delegateRateTitle =
              typeof selectedGroup.name === 'object'
                ? (selectedGroup.name as Record<string, string>)[appConfig.defaults.locale]
                : (selectedGroup.name as string) ?? '';
          }
        }

        const associationKey = generateAssociationKey(
          selectedPass.key ?? '',
          addOnsToAdd.map((addon) => addon.key ?? ''),
          optionNames,
          delegateRateTitle
        );

        const typeId = import.meta.env.VITE_LINE_ITEM_TYPE_ID;
        if (!typeId) return;

        const itemsToBatch: Array<{
          productId: string;
          variantId: number;
          custom: Record<string, unknown>;
          quantity: number;
          externalPrice: { centAmount: number; currencyCode: string };
        }> = [];

        const mainPassCustom: Record<string, unknown> = {
          type: { typeId: 'type', id: typeId },
          fields: {
            associationKey,
            delegateRate: delegateRateTitle,
          },
        };

        if (optionNames.length > 0) {
          (mainPassCustom.fields as Record<string, unknown>)['selectedOptionsList'] = optionNames;
        }

        itemsToBatch.push({
          productId: selectedPass.id,
          variantId: selectedPass.masterVariant?.id ?? 1,
          custom: mainPassCustom,
          quantity,
          externalPrice: { centAmount: basePrice, currencyCode },
        });

        type AddOnWithPrice = ProductWithAddOns & {
          masterData?: {
            current?: {
              masterVariant?: {
                id?: number;
                prices?: Array<{
                  value?: { centAmount?: number; currencyCode?: string };
                  discounted?: { value?: { centAmount?: number } };
                }>;
              };
            };
            staged?: {
              masterVariant?: {
                id?: number;
                prices?: Array<{
                  value?: { centAmount?: number; currencyCode?: string };
                  discounted?: { value?: { centAmount?: number } };
                }>;
              };
            };
          };
          prices?: Array<{
            value?: { centAmount?: number; currencyCode?: string };
            discounted?: { value?: { centAmount?: number } };
          }>;
          price?: {
            value?: { centAmount?: number; currencyCode?: string };
            discounted?: { value?: { centAmount?: number } };
          };
        };

        for (const addOn of addOnsToAdd) {
          const addOnWithPrice = addOn as AddOnWithPrice;
          const addOnPriceObj =
            addOnWithPrice.masterData?.current?.masterVariant?.prices?.[0] ??
            addOnWithPrice.masterData?.staged?.masterVariant?.prices?.[0] ??
            addOnWithPrice.prices?.[0] ??
            addOnWithPrice.price;

          const addOnCentAmount =
            addOnPriceObj?.discounted?.value?.centAmount ??
            addOnPriceObj?.value?.centAmount ??
            0;
          const addOnCurrency = addOnPriceObj?.value?.currencyCode ?? currencyCode;

          let addOnVariantId = 1;
          if (addOnWithPrice.masterData?.current?.masterVariant?.id) {
            addOnVariantId = addOnWithPrice.masterData.current.masterVariant.id;
          } else if (addOnWithPrice.masterData?.staged?.masterVariant?.id) {
            addOnVariantId = addOnWithPrice.masterData.staged.masterVariant.id;
          }

          itemsToBatch.push({
            productId: addOnWithPrice.id,
            variantId: addOnVariantId,
            custom: {
              type: { typeId: 'type', id: typeId },
              fields: {
                associationKey,
                delegateRate: delegateRateTitle,
              },
            },
            quantity,
            externalPrice: { centAmount: addOnCentAmount, currencyCode: addOnCurrency },
          });
        }

        await addItems(itemsToBatch);

        const productName =
          selectedPass.name?.[appConfig.defaults.locale] ??
          selectedPass.name?.['en'] ??
          'Item';
        if (window.innerWidth >= 768) {
          window.dispatchEvent(new Event('openBasket'));
        } else {
          showSuccess(
            SHARE_SUCCESS_MESSAGES.ITEM_ADDED_TITLE,
            SHARE_SUCCESS_MESSAGES.ITEM_ADDED_MESSAGE(productName)
          );
        }

        onSuccess();
      } catch (err) {
        console.error('Failed to add items to cart:', err);
      }
    },
    [selectedPass, selectedRateId, customerGroups, quantity, showSuccess, onSuccess]
  );

  return { handleAddToBasket };
}
